import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/Layout';
import { chatLevelContext } from './lib/chatlevel-context';
import { captureExceptionIfAvailable, initSentryIfAvailable } from './lib/sentry';

/**
 * Last-resort error boundary: si un render lanza, evitamos pantalla en blanco.
 * Capturamos el error, logueamos a consola para diagnóstico (Sentry futuro)
 * y mostramos card centrado con CTA de reload. Errores asíncronos (promises,
 * event handlers) NO los captura React — para eso usamos el manejo en
 * componentes individuales (queries.ts → Layout.ErrorState).
 */
interface ErrorBoundaryState {
  error: Error | null;
}
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[ErrorBoundary] caught render error', error, info.componentStack);
    // Sentry: si está activo (env VITE_SENTRY_DSN + paquete instalado),
    // reporta el error con el component stack como extra. NO-op si no.
    captureExceptionIfAvailable(error, { componentStack: info.componentStack });
  }

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error.message || 'Error desconocido';
    return (
      <main
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          background: 'var(--bg-0)',
          color: 'var(--text-1)',
          fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: '100%',
            background: 'var(--bg-1)',
            border: '1px solid var(--border-2)',
            borderRadius: 8,
            padding: 24,
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: 18 }}>Ocurrió un error inesperado</h2>
          <p style={{ color: 'var(--text-2)', fontSize: 13, lineHeight: 1.5 }}>
            La aplicación se topó con un problema y no pudo continuar.
            Recarga la página para intentarlo de nuevo. Si persiste, contacta soporte.
          </p>
          <pre
            style={{
              marginTop: 12,
              padding: 8,
              background: 'var(--bg-2)',
              borderRadius: 4,
              fontSize: 11,
              color: 'var(--text-3)',
              textAlign: 'left',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {msg}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              background: 'var(--accent, #2563eb)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Recargar
          </button>
        </div>
      </main>
    );
  }
}
// F9-C: route-level code-split via React.lazy. Cada ruta se baja en su propio
// chunk on-demand; el initial bundle baja ~500 KB. La mayoría de rutas usa
// `export function Foo()` (named), por eso envolvemos en `.then(m => ({ default: m.Foo }))`.
// `EmbedError` y `OauthSuccess` ya exportaban default y van directo.
// ApiKeys / Atribucion / Audit / ConversionPlatforms / Team / Webhooks — los
// routes top-level se convirtieron en redirects a /configuracion?tab=<X>. Los
// componentes siguen existiendo y los carga Configuracion.tsx como sub-tabs
// vía su propio import lazy. NO importar acá: TS strict (`noUnusedLocals`)
// fallaría y romper el contrato es exactamente lo que queremos evitar.
const Configuracion = React.lazy(() =>
  import('./routes/Configuracion').then((m) => ({ default: m.Configuracion })),
);
const Dashboard = React.lazy(() =>
  import('./routes/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const Descuentos = React.lazy(() =>
  import('./routes/Descuentos').then((m) => ({ default: m.Descuentos })),
);
const Categories = React.lazy(() =>
  import('./routes/Categories').then((m) => ({ default: m.Categories })),
);
const ProductImagesBulk = React.lazy(() =>
  import('./routes/ProductImagesBulk').then((m) => ({ default: m.ProductImagesBulk })),
);
const Channels = React.lazy(() =>
  import('./routes/Channels').then((m) => ({ default: m.Channels })),
);
const Embed = React.lazy(() =>
  import('./routes/Embed').then((m) => ({ default: m.Embed })),
);
const EmbedError = React.lazy(() => import('./routes/EmbedError'));
const Imports = React.lazy(() =>
  import('./routes/Imports').then((m) => ({ default: m.Imports })),
);
const Leads = React.lazy(() =>
  import('./routes/Leads').then((m) => ({ default: m.Leads })),
);
const Clientes = React.lazy(() =>
  import('./routes/Clientes').then((m) => ({ default: m.Clientes })),
);
const OauthSuccess = React.lazy(() => import('./routes/OauthSuccess'));
const Login = React.lazy(() =>
  import('./routes/Login').then((m) => ({ default: m.Login })),
);
const Movements = React.lazy(() =>
  import('./routes/Movements').then((m) => ({ default: m.Movements })),
);
const Payments = React.lazy(() =>
  import('./routes/Payments').then((m) => ({ default: m.Payments })),
);
const PriceLists = React.lazy(() =>
  import('./routes/PriceLists').then((m) => ({ default: m.PriceLists })),
);
const Products = React.lazy(() =>
  import('./routes/Products').then((m) => ({ default: m.Products })),
);
const Provision = React.lazy(() =>
  import('./routes/Provision').then((m) => ({ default: m.Provision })),
);
const ReorderPoints = React.lazy(() =>
  import('./routes/ReorderPoints').then((m) => ({ default: m.ReorderPoints })),
);
const Setup = React.lazy(() =>
  import('./routes/Setup').then((m) => ({ default: m.Setup })),
);
const SupportCases = React.lazy(() =>
  import('./routes/SupportCases').then((m) => ({ default: m.SupportCases })),
);
const SupportCaseDetail = React.lazy(() =>
  import('./routes/SupportCases.detail').then((m) => ({
    default: m.SupportCaseDetail,
  })),
);
const Suppliers = React.lazy(() =>
  import('./routes/Suppliers').then((m) => ({ default: m.Suppliers })),
);
const Warehouses = React.lazy(() =>
  import('./routes/Warehouses').then((m) => ({ default: m.Warehouses })),
);
// V3.6 — routes top-level que el top nav esconde (acceso via dropdown
// "Configuración" del nav). Siguen como rutas full-width independientes —
// NO embebidos en /configuracion para evitar el riesgo de quedar angostos.
const ApiKeys = React.lazy(() =>
  import('./routes/ApiKeys').then((m) => ({ default: m.ApiKeys })),
);
const Webhooks = React.lazy(() =>
  import('./routes/Webhooks').then((m) => ({ default: m.Webhooks })),
);
const Team = React.lazy(() =>
  import('./routes/Team').then((m) => ({ default: m.Team })),
);
const Audit = React.lazy(() =>
  import('./routes/Audit').then((m) => ({ default: m.Audit })),
);
const Atribucion = React.lazy(() =>
  import('./routes/Atribucion').then((m) => ({ default: m.Atribucion })),
);
const ConversionPlatforms = React.lazy(() =>
  import('./routes/ConversionPlatforms').then((m) => ({ default: m.ConversionPlatforms })),
);
// Landing-pedido público V3 — path slug. Pública, FUERA de <Layout/>.
const RealizarPedido = React.lazy(() =>
  import('./routes/RealizarPedido').then((m) => ({ default: m.RealizarPedido })),
);
const RealizarPedidoGracias = React.lazy(() =>
  import('./routes/RealizarPedidoGracias').then((m) => ({
    default: m.RealizarPedidoGracias,
  })),
);
// Storefront legacy embebido (/tienda/*) ELIMINADO 2026-07-01: lo reemplazó el
// storefront real (web/ → inventario.whatsfy.co). /realizar-pedido sigue vivo.

import {
  forceLightMode,
  initTheme,
  isStorefrontPath,
  subscribeToParentTheme,
} from './lib/theme';
import './styles/tokens.css';
import './styles/global.css';

/**
 * Fallback minimalista durante chunk-load de una ruta lazy. Spinner CSS-only
 * sin texto para evitar flash. Hereda colores del theme.
 */
function RouteFallback(): JSX.Element {
  return (
    <main
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-busy="true"
      aria-label="Cargando"
    >
      <div
        style={{
          width: 28,
          height: 28,
          border: '3px solid var(--border-2, #e5e7eb)',
          borderTopColor: 'var(--accent, #2563eb)',
          borderRadius: '50%',
          animation: 'route-fallback-rotate 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes route-fallback-rotate { to { transform: rotate(360deg); } }`}</style>
    </main>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

// Inicializar tema antes de createRoot para evitar flash. Detalles de la
// estrategia (manual override > hint del parent > prefers-color-scheme) en
// lib/theme.ts.
//
// Excepción load-bearing: el storefront público (`/tienda/*`) es admin-light-only.
// Heredar `data-theme="dark"` del panel admin rompe el branding del tenant y
// los contrastes (bgs oscuros sobre fotos de productos). NO llamamos initTheme
// si la URL inicial es de storefront, y forzamos light explícito.
if (isStorefrontPath(window.location.pathname)) {
  forceLightMode();
} else {
  initTheme();
  // Si Whatsfy eventualmente coopera y postMessagea el tema, lo
  // aplicamos automático respetando override manual del user.
  subscribeToParentTheme();
}

// Listener de navegación SPA: cuando el user navega del admin a `/tienda/*`
// (sin reload), react-router usa history.pushState — sin esto, el dark mode
// activo del admin se quedaría aplicado al storefront. Monkeypatch suave de
// pushState + replaceState + popstate cubre los 3 caminos de navegación.
// Trade-off: NO restauramos el theme admin al volver de `/tienda/*` a
// `/dashboard` (requeriría persistir el theme previo); el usuario verá light
// hasta que recargue o togglee manualmente. Aceptable: el caso primario es
// abrir un link directo a `/tienda/*`, no navegar internamente.
if (typeof window !== 'undefined') {
  const applyThemeForPath = (): void => {
    if (isStorefrontPath(window.location.pathname)) {
      forceLightMode();
    }
  };
  window.addEventListener('popstate', applyThemeForPath);
  const origPushState = history.pushState;
  history.pushState = function (
    ...args: Parameters<typeof history.pushState>
  ): ReturnType<typeof history.pushState> {
    const ret = origPushState.apply(this, args);
    applyThemeForPath();
    return ret;
  };
  const origReplaceState = history.replaceState;
  history.replaceState = function (
    ...args: Parameters<typeof history.replaceState>
  ): ReturnType<typeof history.replaceState> {
    const ret = origReplaceState.apply(this, args);
    applyThemeForPath();
    return ret;
  };
}
// Sentry: NO-op si VITE_SENTRY_DSN no está seteado o el paquete @sentry/react
// no está instalado. Fire-and-forget (no awaitamos para no bloquear primer
// paint; el ErrorBoundary capturará errores incluso si Sentry aún no terminó
// de inicializar — los reportes se acumulan en el queue interno del SDK).
void initSentryIfAvailable();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={chatLevelContext()?.basename ?? '/'}>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/embed" element={<Embed />} />
            <Route path="/embed-error" element={<EmbedError />} />
            <Route path="/oauth-success" element={<OauthSuccess />} />
            <Route path="/login" element={<Login />} />
            <Route path="/provision" element={<Provision />} />
            {/* Landing-pedido público — sin Layout, sin auth */}
            <Route
              path="/realizar-pedido/:slug/:sku"
              element={<RealizarPedido />}
            />
            <Route
              path="/realizar-pedido/:slug/:sku/gracias/:paymentId"
              element={<RealizarPedidoGracias />}
            />
            {/* V3.1 — storefront browseable público. Sin Layout, sin auth. */}
            <Route element={<Layout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/products" element={<Products />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/warehouses" element={<Warehouses />} />
              <Route path="/suppliers" element={<Suppliers />} />
              <Route path="/movements" element={<Movements />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/price-lists" element={<PriceLists />} />
              <Route path="/descuentos" element={<Descuentos />} />
              <Route path="/imports" element={<Imports />} />
              <Route path="/products/imagenes" element={<ProductImagesBulk />} />
              <Route path="/channels" element={<Channels />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/clientes" element={<Clientes />} />
              <Route path="/casos" element={<SupportCases />} />
              <Route path="/casos/:id" element={<SupportCaseDetail />} />
              <Route path="/reorder-points" element={<ReorderPoints />} />
              <Route path="/configuracion" element={<Configuracion />} />
              {/* V3.6 — top nav simplificado: estos 7 dejaron de aparecer en
                  el top nav (Layout.tsx ahora muestra solo 13 tabs). Siguen
                  siendo routes top-level full-width — acceso vía dropdown
                  "Configuración" del top nav o via URL directa. NO embebidos
                  en /configuracion para evitar el riesgo de quedar angostos
                  con doble sidebar (cazado al planear SA #58). */}
              <Route path="/equipo" element={<Team />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/auditoria" element={<Audit />} />
              <Route path="/api-keys" element={<ApiKeys />} />
              <Route path="/webhooks" element={<Webhooks />} />
              <Route path="/atribucion" element={<Atribucion />} />
              <Route path="/conversion-platforms" element={<ConversionPlatforms />} />
              <Route path="/plataformas-ads" element={<ConversionPlatforms />} />
              {/* `/setup` se preserva como ruta directa: el Layout redirige
                  aquí a los tenants con `connected=false` (onboarding inicial,
                  pre-handshake). Cambiar /setup a un redirect crearía bucle
                  infinito con la guardia connected-check de Layout. */}
              <Route path="/setup" element={<Setup />} />
              {/* Alias legacy: el tab conexion_whatsfy nunca existió en
                  Configuración (deep-link roto → contenido en blanco). La
                  página real de conexión es /setup. */}
              <Route
                path="/conexion-whatsfy"
                element={<Navigate to="/setup" replace />}
              />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
